const catchify = require('catchify');
const omit = require('lodash/omit');

const { sleep } = require('./helpers');

const defaultHeaders = {};
let userToken = null;

/**
 * Set global userToken and `tgb-token` header if token is not null/undefined.
 * @param {String} token - User token to use for requests as `tgb-token` header.
 */
const setUserToken = (token) => {
  userToken = token;

  if (userToken) {
    defaultHeaders['tgb-token'] = userToken;
  } else {
    // Don't include `tgb-token` if we don't have a token.
    // Some of our auth plugins don't like an undefined header.
    delete defaultHeaders['tgb-token'];
  }
};

const requestAuth = () => {
  if (userToken !== null) {
    return {
      xhrFields: {
        withCredentials: true,
      },
      headers: defaultHeaders,
    };
  }

  return null;
};

const request = (options) => {
  if (options === null) {
    throw new Error('wut?!?');
  }

  const authbits = requestAuth();

  if (authbits) { // or undefined
    $.extend(true, options, authbits);
  }

  return $.ajax(options);
};

const DefaultRequestOpts = (headers = {}) => ({
  credentials: 'include',
  mode: 'cors',
  headers: {
    ...headers,
    ...defaultHeaders,
  },
});

const buildQueryString = (opts = {}) => Object.keys(opts).reduce((memo, key) => (
  (key && opts[key]) ? `${memo}${key}=${encodeURIComponent(opts[key])}&` : memo
), '?');

const MAX_RETRIES = 3;
const RETRY_DELAY = 500;

/**
 * Make an HTTP request using Fetch API.
 * @param {String} url - URL to hit.
 * @param {Object} options - Additional data to merge into request payload
 * @param {Number} attempts - Current number of attempts. Used to retry failed requests.
 * @returns {Object} JSON response from server.
 */
async function requestFetch(url, options, attempts = 0) {
  if (options?.query) {
    /* eslint-disable no-param-reassign */
    url += `${buildQueryString(options.query)}`;
    delete options.query;
  }

  const [fetchErr, resp] = await catchify(
    fetch(url, {
      ...DefaultRequestOpts(options?.headers),
      ...omit(options, ['headers']),
    }),
  );

  // fetchErr = "Failed to Fetch" error (most likely). Possibly badly formed URL.
  if (fetchErr || !resp.ok) {
    const method = options?.method || 'GET';

    // Only retry GET requests. We don't want anything possibly destructive ran over and over.
    // Also don't retry if the status returned is "401 Unauthorized"
    if (attempts < MAX_RETRIES && method === 'GET' && resp?.status !== 401) {
      await sleep(RETRY_DELAY);
      return requestFetch(url, options, attempts + 1);
    }

    if (fetchErr) {
      throw new Error(`Failed to fetch ${url} with error: ${fetchErr.message}`);
    }

    const err = new Error(`Request to ${url} failed with status ${resp.status} ${resp.statusText}`);
    err.status = resp.status;
    throw err;
  }

  return resp.status === 204 ? {} : resp.json();
}

export {
  setUserToken,
  request,
  requestFetch,
  requestFetch as fetch,
  buildQueryString,
};
